import React, { useState, useEffect, useRef } from 'react';
import { List, ListItem, TextField, Button, Box, Typography, CircularProgress, Avatar, 
  FormControlLabel, Checkbox, IconButton, Alert, Divider } from '@mui/material';
import axios from 'axios';
import { Person, Computer, Send, Close } from '@mui/icons-material';
import { colorScaleOptions } from '../shared/utilities/Colors';
import { isHtmlElement } from 'react-router-dom/dist/dom';

function getCookie(cname: any) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for(let i = 0; i <ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

// Define the type of the API response
interface AIResponse {
  message: { 
    content: string, 
    tool_calls: {
        id: string,
        type: string
        function: {
          name: string, 
          arguments: string
        }
    }[]
  };
  error?: string
}

interface AssistantProps {
  plots?: any;
  updatePlotConfig: (plotID: string, config: any) => void;
  setPlots: React.Dispatch<React.SetStateAction<any>>; 
  selectedMultiPlot: number;
  handleResetPlot: (plotID: number) => void;
  plotConfigs: { [key: string]: PlotConfig };
  treatmentOptions: SearchableDropdownOption[];
  setTreatment: (treatmentOptions: SearchableDropdownOption[]) => void;
  timepointOptions: SearchableDropdownOption[];
  setTimepoint: (timepointOptions: SearchableDropdownOption[]) => void;
  setGenesNS: (geneOptionsNS: SearchableDropdownOption[]) => void; 
  geneOptionsNS: SearchableDropdownOption[];
  setGenesRNASEQ: (geneOptionsRNASEQ: SearchableDropdownOption[]) => void; 
  geneOptionsRNASEQ: SearchableDropdownOption[]; 
}

const Assistant: React.FC<AssistantProps> = ({plots, updatePlotConfig, setPlots, selectedMultiPlot, handleResetPlot, plotConfigs, treatmentOptions, setTreatment, timepointOptions, 
                                              setTimepoint, setGenesNS, geneOptionsNS, setGenesRNASEQ, geneOptionsRNASEQ}) => {

  const [message, setMessage] = useState<string>(''); // User provided question or command 
  const [conversation, setConversation] = useState<{ user: string; ai: string | null }[]>([]); // Conversation history
  const [loading, setLoading] = useState<boolean>(false); 
  const [error, setError] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false); 
  const [plotData, setPlotData] = useState<any>(null);  // Current state of plot data
  const [plotLayout, setPlotLayout] = useState<any>(null); // Current state of plot layout
  const [formatPlot, setFormatPlot] = useState<boolean>(false);  // UI setting which controls if plot data is sent to the AI Assistant API
  const [functionCalling, setFunctionCalling] = useState<boolean>(true); // UI setting to control function calling
  const [initialPlotConfig, setInitialPlotConfig] = useState<{ [key: string]: PlotConfig } | null>(null);

  useEffect(() => {
    if (plotConfigs) {
      // console.log("initialPlotConfig: ", plotConfigs);
      setInitialPlotConfig(plotConfigs)
    }
  },[])

  // // Scroll to the top of the last user message or the loading icon
  useEffect(() => {
    const chatContainer = document.getElementById('chat-container');
    const loadingElement = document.getElementById('loading-icon'); // Target the loading icon
    const lastConversationTopElement = document.querySelector(
      `[data-conversation-id="${conversation.length - 1}"]`
    );
  
    if (chatContainer) {
      let scrollTarget;
  
      // Prioritize the loading icon if it exists and is active
      if (loadingElement) {
        scrollTarget = loadingElement;
      } else if (lastConversationTopElement) {
        scrollTarget = lastConversationTopElement;
      }
  
      if (scrollTarget) {
        const containerRect = chatContainer.getBoundingClientRect();
        const targetRect = scrollTarget.getBoundingClientRect();
  
        // Calculate the offset to scroll within the chat container
        const offset = targetRect.top - containerRect.top;
  
        chatContainer.scrollBy({
          top: offset,
          behavior: 'smooth',
        });
      }
    }
  }, [conversation, loading]);  

  useEffect(() => {

    if (plots[0]) {
      if ('plotLayers' in plots[0]) {
        const plotData = plots[0].plotLayers;
        setPlotData(plotData);
      }
    }

    if (plots[0]) {
      if ('plotLayout' in plots[0]) {
        const plotLayout = plots[0].plotLayout;
        setPlotLayout(plotLayout);
      }
    }
  }, [plots]);

  const handleReset = () => {
    handleResetPlot(selectedMultiPlot);
    if (initialPlotConfig) {
      updatePlotConfig('plot1', initialPlotConfig['plot1']);
    }
    setTreatment(treatmentOptions);
    setTimepoint(timepointOptions)
    // setInitialPlotConfig(plotConfigs);
  }

  const handleSubmit = async () => {
    if (!message) return; // Prevent submission if no message is typed
    setLoading(true);
    setError(null);
    setAlertOpen(false); 

    let api_message = message;

    // Include the conversation history along with the current message
    const conversationHistory = conversation.flatMap(entry => [
      { role: 'user', content: entry.user },
      { role: 'assistant', content: entry.ai }
    ]).concat({ role: 'user', content: message });

    // console.log("conversationHistory: ", conversationHistory);

    if (formatPlot) {
      // api_message = "User message: " + message + ". Current Plotly Data: " + JSON.stringify(plotData);
      api_message = "User message: " + message + ". Current Plotly Data (Layers) and Plotly Layout: " + JSON.stringify({plotLayers: plotData, plotLayout: plotLayout});
      
    }

    let messages = [...conversationHistory, {role: 'user', content: api_message}];

    const handlePlotData = (result: any, message: string, updatedData: boolean, updatedLayout: boolean) => {
      const regex = /```json\n([\s\S]*?)\n```/;
      const matchedData = result.data.message.content.match(regex);
    
      // console.log("matchedData: ", matchedData);
    
      if (matchedData) {
        // console.log("Found JSON in AI response");
        
        if (updatedData) {
          // console.log("updating plot data...");
          const updatedPlotData = JSON.parse("" + matchedData[1]).data; 
          // Update the state of the plot data in the parent component
          setPlots((prev: any) => [
            {
              ...prev[0],
              plotLayers: updatedPlotData
            },
          ]);
        } 

        if (updatedLayout) {
          // console.log("Updating plot layout...");
          // console.log("matchedData[1]: ", matchedData[1]);
          const updatedPlotLayout = JSON.parse("" + matchedData[1]).layout; 

          // console.log("updatedPlotLayout: ", updatedPlotLayout);
          // console.log("plots: ", plots);
          setPlots((prev: any) => [
            {
              ...prev[0],
              plotLayout: updatedPlotLayout
            },
          ]);
        } 
    
        setConversation((prev) => [...prev, { user: message, ai: "Done!" }]);
        setMessage('');
      } else {
        // console.log('No plot data found in the content.');
        setConversation((prev) => [
          ...prev,
          { user: message, ai: result.data.message.content ? result.data.message.content : '' },
        ]);
        setMessage('');
      }
    };

    // Function to check for the presence of a function call
    function hasFunctionCall(messageObj: any): boolean {
      // Ensure messageObj has a tool_calls array
      if (messageObj && Array.isArray(messageObj.tool_calls)) {
        return messageObj.tool_calls.some((call: any) =>
          call.type === "function"
        );
      }
      return false;
    }

    try {
      const result = await axios.post<AIResponse>("/tumors/api/v1/ai/assistant/", 
        { 
          messages: messages, 
          function_calling: functionCalling, 
          treatment_value_options: treatmentOptions.map(item => item.value),
          timepoint_value_options: timepointOptions.map(item => item.value),
          colorscale_options: colorScaleOptions.map(item => item.value),
          // gene_value_options: geneOptionsNS.map((item: any) => item.value),
        }, 
        {headers: {"X-CSRFToken": getCookie("csrftoken")}});

      // Look for a function call in the response
      const initialFunctionCall = hasFunctionCall(result.data.message) ? result.data.message.tool_calls[0].function : null;

      // Only parse for updated plot data if the user had submitted a formatting request
      if (initialFunctionCall) {
        const initialFunctionArguments = JSON.parse(initialFunctionCall.arguments);

        if (initialFunctionCall.name === 'update_plot') {
          // console.log("calling update plot function");
          if (initialFunctionArguments.hasOwnProperty('color')) {
            initialFunctionArguments.fixedColorMode = true;
            initialFunctionArguments.fixedColor = initialFunctionArguments.color;
            delete initialFunctionArguments.color;
          }

          if (initialFunctionArguments.hasOwnProperty('pvalue')) {
            if (initialFunctionArguments.pvalue) {
              initialFunctionArguments.pvalue = "control"
              initialFunctionArguments.pvalueDisplayMode = "lines+text"
            } else {
              initialFunctionArguments.pvalue = undefined;
              // pvalueColumn: undefined,
              // pvalueControl: undefined,
              // pvalueMethod: 'ttest',
              // pvalueHideInsignificant: false,
              // pvalueDisplayMode: 'lines+text',
              // pvalueLegend: false,
            }
          }

          updatePlotConfig('plot1', initialFunctionArguments)
          setMessage('');
          setConversation((prev) => [...prev, { user: message, ai: "Done!" }]);

        } else if (initialFunctionCall.name === 'provide_plot_data') {

          api_message = '';
          let updatedPlotData = false;
          let updatedPlotLayout = false;
          
          if (initialFunctionArguments.plotData_requested) {
            api_message = "Here is the Plotly Data: " + JSON.stringify({data: plotData}) + ". ";
            updatedPlotData = true;
          }

          if (initialFunctionArguments.plotLayout_requested) {
            api_message = api_message + " Here is the Plotly Layout: " + JSON.stringify({layout: plotLayout});
            updatedPlotLayout = true;
          };

          messages = [...conversationHistory, {role: 'user', content: api_message}];
          const result = await axios.post<AIResponse>("/tumors/api/v1/ai/assistant/", { messages: messages, function_calling: false }, {headers: {"X-CSRFToken": getCookie("csrftoken")}});
          handlePlotData(result, message, updatedPlotData, updatedPlotLayout);
          return;

        } else if (initialFunctionCall && initialFunctionCall.name && initialFunctionCall.name=== 'reset') {
          handleReset();
          setConversation(prev => [...prev, { user: message, ai: 'Successfully reset plot' }]);
          setMessage('');
          return;
        } else if (initialFunctionCall && initialFunctionCall.name && initialFunctionCall.name=== 'set_treatments') {
          const filteredTreatments = treatmentOptions.filter(option => initialFunctionArguments.treatment_values.includes(option.value));
          setTreatment(filteredTreatments);
          setConversation(prev => [...prev, { user: message, ai: 'Successfully set filtered treatments' }]);
          setMessage('');
          return;
        } else if (initialFunctionCall && initialFunctionCall.name && initialFunctionCall.name=== 'set_timepoints') {
          const filteredTimepoints = timepointOptions.filter(option => initialFunctionArguments.timepoint_values.includes(option.value));
          setTimepoint(filteredTimepoints);
          setConversation(prev => [...prev, { user: message, ai: 'Successfully set filtered timepoints' }]);
          setMessage('');
          return;
        } else if (initialFunctionCall && initialFunctionCall.name && initialFunctionCall.name=== 'set_gene_filter') {
          const filteredGenesNS = (geneOptionsNS ?? []).filter((option: any) => initialFunctionArguments.gene_values.includes(option.value));
          setGenesNS(filteredGenesNS);
          const filteredGenesRNASEQ = (geneOptionsRNASEQ ?? []).filter((option: any) => initialFunctionArguments.gene_values.includes(option.value));
          setGenesRNASEQ(filteredGenesRNASEQ);
          setConversation(prev => [...prev, { user: message, ai: 'Successfully set custom filter' }]);
          setMessage('');
          return;
        }
      } else {
        setConversation(prev => [...prev, { user: message, ai: result.data.message.content ? result.data.message.content : '' }]);
        setMessage('');
      }

    } catch (err: any) {
      console.log(err);
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false); // Stop loading state
    }
  };

  // Handle key press to submit on "Enter"
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
  if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); 
      handleSubmit(); 
  }
  };


  const handleAlertClose = () => {
    setAlertOpen(false);
    setError(null);
  }

  const handleClearConversation = () => {
    setMessage('');
    setConversation([]);
    setAlertOpen(false);
    setError(null);
  }

  const handleFormatPlotSetting = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormatPlot(event.target.checked);
    setFunctionCalling(false);
  };

  const handleFunctionCalling = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFunctionCalling(event.target.checked);
    setFormatPlot(false);
  };

  // Function to format text with bold, bullets, and subtitles while keeping numbering
  const formatTextWithBoldBulletsAndSubtitles = (text: string) => {
    const lines = text.split('\n').map((line) => {
      if (line.startsWith('###')) {
        const subtitle = line.slice(3).trim();
        return `<h2 style="font-size: 1.2rem; padding-top: 20px;">${subtitle}</h2>`;
      } else if (/^\d+\./.test(line)) {
        const formattedLine = line
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
          .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic text
          .replace(/\[(.*?)\]\((http[s]?:\/\/[^\s]+|mailto:[^\s]+)\)/g, '<a href="$2" style="color: blue;">$1</a>') // Hyperlink + Mailto
        return `<div style="padding-top: 10px;">${formattedLine}</div>`;
      } else {
        return line
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold text
          .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic text
          .replace(/\[(.*?)\]\((http[s]?:\/\/[^\s]+|mailto:[^\s]+)\)/g, '<a href="$2" style="color: blue;">$1</a>') // Hyperlink + Mailto
          .replace(/- (.*?)(?=\n|$)/g, '<li style="padding: 5px 0;">$1</li>')
          .replace(/(<li>.*?<\/li>)/g, '<ul style="padding-left: 40px; list-style-type: disc; margin: 0;">$1</ul>');
      }
    });
  
    return { __html: lines.join('') };
  };
  
  
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', padding: 2, height: '90vh', overflowY: 'hidden'}}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 0, paddingBottom: 0 }}>
          <TextField
            label="Type in your question or command"
            fullWidth
            variant="outlined"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            multiline
            rows={4}
            sx={{ marginBottom: 0, paddingBottom: 0 }}
          />
        </Box>
        <Box sx={{ padding: 0, margin: 0}}>

          {/* <FormControlLabel
            control={
              <Checkbox
                checked={formatPlot}
                onChange={handleFormatPlotSetting}
                color="secondary"
                sx={{ transform: 'scale(0.8)' }}
              />
            }
            label={<Typography variant='body2' sx={{ fontSize: 12, padding: 0 }}>Provide Plot</Typography>}
          /> */}

          {/* <FormControlLabel
            control={
              <Checkbox
                checked={functionCalling}
                onChange={handleFunctionCalling}
                color="secondary"
                sx={{ transform: 'scale(0.8)' }}
              />
            }
            label={<Typography variant='body2' sx={{ fontSize: 12, padding: 0 }}>Function Calling</Typography>}
          /> */}
        
          <Button 
            variant="text" 
            size="small" 
            sx={{ marginLeft: 1, fontSize: '0.75rem' }} 
            onClick={handleClearConversation}
          >
            Clear
          </Button>

          <Button 
            variant="text" 
            size="small" 
            sx={{ marginLeft: 1, fontSize: '0.75rem' }} 
            onClick={handleReset}
          >
            Reset Plot
          </Button>
        </Box>
      </Box>


      <Box id="chat-container" sx={{ flexGrow: 1, marginTop: 4, textAlign: 'left', height: '100%', overflowY: 'scroll' }}>
        
        <List sx={{ padding: 0 }}>
          {conversation.map((entry, index) => (
            <Box key={index} data-conversation-id={index}>
              <ListItem key={index} sx={{ display: 'block', marginBottom: 2, padding: 0 }}>
                {entry.user && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', position: 'relative', padding: '8px', backgroundColor: '#e7f1fe', borderRadius: '10px', maxWidth: '100%', marginBottom: 2 }}>
                    <Avatar sx={{ backgroundColor: '#2ab8f3', width: 30, height: 30, fontSize: "20px"}}>
                      <Person sx={{fontSize: "20px"}}/>
                    </Avatar>
                    <Typography variant="body1" sx={{ paddingLeft: 2 }}>
                      {entry.user}
                    </Typography>
                  </Box>
                )}
                {entry.ai && (
                  <Box sx={{ display: 'flex', justifyContent: "flex-end", position: 'relative', padding: '8px', backgroundColor: 'white', borderRadius: '10px', maxWidth: '100%', marginBottom: 2 }}>
                    {/* <Avatar sx={{ backgroundColor: '#2ab8f3', width: 30, height: 30, fontSize: "20px"}}>
                      <Computer sx={{fontSize: "20px"}}/>
                    </Avatar> */}
                    <Typography variant="body1" sx={{ paddingLeft: 2, whiteSpace: 'pre-line' }}>
                      <span dangerouslySetInnerHTML={formatTextWithBoldBulletsAndSubtitles(entry.ai)} />
                    </Typography>
                  </Box>
                )}
              </ListItem>
            </Box>
          ))}
        </List>

        {loading && (
          <Box id="loading-icon" sx={{ display: 'flex', justifyContent: "center", position: 'relative', padding: '8px', backgroundColor: 'white', borderRadius: '10px', maxWidth: '100%', marginBottom: 2 }}>
            <CircularProgress size={36} />
          </Box>
        )}

        {error && ( 
          <Alert 
            severity="error"
            sx={{ marginBottom: 2 }}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleAlertClose}
              >
                <Close fontSize="small" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}
      </Box>
      <Box>
        <Divider sx={{ marginTop: 2, borderBottomWidth: 1, borderColor: 'primary.main' }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', position: 'relative', padding: '8px', backgroundColor: 'white', borderRadius: '10px', maxWidth: '100%', marginBottom: 2 }}>
          <Typography variant='body2' sx={{fontSize: '0.7rem', color: 'grey', fontStyle: 'italic'}}>Xsphera AI can make mistakes. Validate important findings.</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Assistant;
